import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useNavigationContainerRef } from 'expo-router';

import { useIsFocused } from '@sg/garnish';

import type { ScreenOptions } from '../../constants';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the current screen options while the caller's navigator is focused.
 * Retains those options while another navigator is presented over it.
 *
 * NOTE: Re-reads the options after every navigation state change, since a tab
 *       bar no longer re-renders when a nested stack navigates and the
 *       container no longer emits an `options` event.
 */
export const useActiveScreenOptions = () => {
  const navigationRef = useNavigationContainerRef();
  const isFocused = useIsFocused();

  const isReady = isFocused && navigationRef.isReady();

  // ─── Refs ─────────────────────────────────────────────────────────────────

  const screenOptionsRef = useRef<ScreenOptions | undefined>(
    isReady ? navigationRef?.getCurrentOptions?.() : undefined,
  );

  // ─── State ────────────────────────────────────────────────────────────────

  const [, rereadScreenOptions] = useReducer(incrementCount, 0);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const subscribeToNavigationState = useCallback(
    () => navigationRef.addListener('state', rereadScreenOptions),
    [navigationRef],
  );

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(subscribeToNavigationState, [subscribeToNavigationState]);

  // ──────────────────────────────────────────────────────────────────────────

  if (isReady) {
    screenOptionsRef.current = navigationRef?.getCurrentOptions?.();
  }

  return screenOptionsRef.current;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function incrementCount(count: number) {
  return count + 1;
}
